import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { fetchUserProfile } from '../services/userService';

// Import translations with error handling
let slTranslations, enTranslations, deTranslations;
try {
    slTranslations = require('../translations/sl.json');
} catch (error) {
    console.error('[LanguageContext] FAILED to load sl.json:', error);
    slTranslations = {};
}

try {
    enTranslations = require('../translations/en.json');
} catch (error) {
    console.error('[LanguageContext] FAILED to load en.json:', error);
    enTranslations = {};
}

try {
    deTranslations = require('../translations/de.json');
} catch (error) {
    console.error('[LanguageContext] FAILED to load de.json:', error);
    deTranslations = {};
}

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState('en');
    // Use useRef to store translations without causing re-renders
    const translationsRef = React.useRef(enTranslations);
    const [, forceUpdate] = useState({});

    // Load user's preferred language on mount
    useEffect(() => {
        const loadUserLanguage = async () => {
            try {
                const userData = await fetchUserProfile();
                if (userData && userData.preferred_language) {
                    const lang = userData.preferred_language;
                    setLanguage(lang);
                    translationsRef.current = lang === 'sl' ? slTranslations : lang === 'de' ? deTranslations : enTranslations;
                    forceUpdate({});
                }
            } catch (error) {
                // If not logged in or error, use default (en)
                // Silently fail and use default language (already set)
            }
        };

        loadUserLanguage();
    }, []);

    const changeLanguage = useCallback((lang) => {
        try {
            setLanguage(lang);
            translationsRef.current = lang === 'sl' ? slTranslations : lang === 'de' ? deTranslations : enTranslations;
            forceUpdate({});
        } catch (error) {
            console.error('Error changing language:', error);
            // Fallback to English if there's an error
            setLanguage('en');
            translationsRef.current = enTranslations;
            forceUpdate({});
        }
    }, []);

    // Helper function to get nested translation with variable interpolation
    // Use useCallback without translations dependency to keep it stable
    const t = useCallback((key, variables = {}) => {
        try {
            if (!key) return '';

            // Get current translations from ref
            const currentTranslations = translationsRef.current || enTranslations;

            if (!currentTranslations || typeof currentTranslations !== 'object') {
                console.error('[t] CRITICAL: Translations not available for key:', key);
                return key; // Return key instead of throwing
            }

            const keys = key.split('.');
            let result = currentTranslations;

            for (const k of keys) {
                if (result && typeof result === 'object' && k in result) {
                    result = result[k];
                } else {
                    // Translation key not found, return the key itself
                    return key;
                }
            }

            // Handle variable interpolation (e.g., {title} in the string)
            if (typeof result === 'string' && Object.keys(variables).length > 0) {
                return result.replace(/\{(\w+)\}/g, (match, varName) => {
                    return variables[varName] !== undefined ? variables[varName] : match;
                });
            }

            return typeof result === 'string' ? result : key;
        } catch (error) {
            console.error('[t] CRITICAL Translation error:', key, error);
            return key; // Return key instead of throwing
        }
    }, []); // Empty dependency array - function never changes

    const value = useMemo(() => ({
        language,
        setLanguage: changeLanguage,
        t
    }), [language, changeLanguage, t]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
